import Stack, { type StackProps } from "@mui/material/Stack";
import { styled } from "../../../../lib/styled";
import { sectionGap } from "../../spacing";

interface PageContainerProps extends StackProps {
  /** Vertically and horizontally center the content (for loading / empty / hero screens). */
  center?: boolean;
  /** For a page inside a layout that already gives it width and padding: it fills the room it is
   *  given instead of centring a reading column. */
  embedded?: boolean;
}

const PageStack = styled(Stack)<{ $center?: boolean; $embedded?: boolean }>(
  ({ $center, $embedded }) => ({
    maxWidth: $embedded ? "none" : 700,
    marginLeft: $embedded ? 0 : "auto",
    marginRight: $embedded ? 0 : "auto",
    minHeight: $embedded ? 0 : "100dvh",
    // Gentle enter fade so route changes feel progressive, not abrupt.
    // Every page routes through here, so the transition is uniform.
    "@keyframes rcPageIn": {
      from: { opacity: 0, transform: "translateY(6px)" },
      to: { opacity: 1, transform: "translateY(0)" },
    },
    animation: "rcPageIn 0.35s ease both",
    "@media (prefers-reduced-motion: reduce)": { animation: "none" },
    ...($center && { justifyContent: "center", alignItems: "center", textAlign: "center" }),
  })
);

/**
 * The standard page shell: constrained width, full height, vertical flow with a
 * consistent section gap. Pages should rely on the defaults and pass `center`
 * rather than re-specifying layout via `sx`.
 */
export function PageContainer({
  children,
  center = false,
  embedded = false,
  ...rest
}: PageContainerProps) {
  return (
    <PageStack
      gap={{ xs: sectionGap, md: 5 }}
      px={embedded ? 0 : { xs: 2, md: 3 }}
      pt={embedded ? 0 : { xs: 4, md: 6 }}
      pb={{ xs: 8, md: 12 }}
      $center={center}
      $embedded={embedded}
      {...rest}
    >
      {children}
    </PageStack>
  );
}
